/* eslint-disable */
// @ts-nocheck

import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import {
  Column,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
  VisibilityState,
} from '@tanstack/react-table'

import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Area } from '@/interfaces/area'

import { HeaderActions } from './HeaderActions'
import { Navigation } from './Navigation'
import { useTableContext } from './TableContext'

// DOCS: https://ui.shadcn.com/docs/components/data-table#basic-table

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[]
}

const AREA_TABLE_COLUMNS_STORAGE_KEY = 'area-table-columns'

export function DataTable<TData, TValue>({
  columns,
}: DataTableProps<TData, TValue>) {
  const navigate = useNavigate()

  // Use the table context
  const {
    currentPage,
    areaData,
    isLoading,
    skeletonRowCount,
    isFetching,
    isError,
  } = useTableContext()

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [isMobileView, setIsMobileView] = useState(false)

  // Create skeleton columns when loading
  const skeletonColumns = useMemo(() => {
    if (!isLoading) return columns

    return columns.map((column) => {
      // Different width based on column type
      let width = '80%'
      const height = '20px'

      // Special cases for specific columns based on content
      if (column.id === 'id' || column.id === 'operationNumber') width = '60%'
      if (column.id === 'status') width = '100%'
      if (column.id === 'totalToPay' || column.id === 'importe') width = '70%'
      if (column.id === 'totalItems' || column.id === 'icons') width = '40%'

      return {
        ...column,
        cell: () => (
          <div className="flex items-center">
            <Skeleton className={`h-${height} w-${width}`} />
          </div>
        ),
      }
    })
  }, [columns, isLoading])

  // Create skeleton data for loading state
  const skeletonData = useMemo(() => {
    if (!isLoading) return []

    return Array.from({ length: skeletonRowCount }).map((_, index) => ({
      id: `skeleton-${index}`,
    }))
  }, [isLoading, skeletonRowCount])

  // Create a memoized version of the data to ensure stable references
  const tableData = useMemo(() => {
    if (isLoading) {
      return skeletonData as TData[]
    }
    return (areaData?.items as TData[]) || []
  }, [areaData?.items, isLoading, skeletonData])

  // Check for mobile view on mount and window resize
  useEffect(() => {
    const checkViewport = () => {
      setIsMobileView(window.innerWidth < 768)
    }

    checkViewport()
    window.addEventListener('resize', checkViewport)

    return () => {
      window.removeEventListener('resize', checkViewport)
    }
  }, [])

  // Set default column visibility based on screen size
  useEffect(() => {
    if (isMobileView) {
      // On mobile, show only high-priority columns
      const mobileVisibility: VisibilityState = {
        origin: false,
        shipping: false,
        totalItems: false,
        areaSales: false,
        deliveryAt: false,
        paidAt: false,
      }
      setColumnVisibility((prev) => ({ ...prev, ...mobileVisibility }))
    }
  }, [isMobileView])

  // Load column visibility settings from localStorage
  useEffect(() => {
    try {
      const storedColumns = localStorage.getItem(AREA_TABLE_COLUMNS_STORAGE_KEY)
      if (storedColumns) {
        const parsedColumns = JSON.parse(storedColumns)
        // Validate that the parsed data is an object
        if (parsedColumns && typeof parsedColumns === 'object') {
          setColumnVisibility(parsedColumns)
        }
      }
    } catch (error) {
      console.error('Failed to load column visibility settings:', error)
      // Continue with default column visibility on error
    }
  }, [])

  // Handle column visibility changes - both update state and save to localStorage
  const handleColumnVisibilityChange = (
    updatedVisibility:
      | VisibilityState
      | ((prev: VisibilityState) => VisibilityState)
  ) => {
    // First update the state
    setColumnVisibility(updatedVisibility)

    // Then save to localStorage
    try {
      // If it's a function, we need to get the actual value
      const newVisibility =
        typeof updatedVisibility === 'function'
          ? updatedVisibility(columnVisibility)
          : updatedVisibility

      if (Object.keys(newVisibility).length > 0) {
        localStorage.setItem(
          AREA_TABLE_COLUMNS_STORAGE_KEY,
          JSON.stringify(newVisibility)
        )
      }
    } catch (error) {
      console.error('Failed to save column visibility settings:', error)
      // Continue without saving settings on error
    }
  }

  // Reset table when the page changes by recreating the table instance
  const table = useReactTable({
    data: tableData,
    columns: isLoading ? (skeletonColumns as ColumnDef<TData, any>[]) : columns,
    getCoreRowModel: getCoreRowModel(),
    onColumnVisibilityChange: handleColumnVisibilityChange,
    state: {
      columnVisibility,
    },
    // This key ensures the table is reset when data changes
    manualPagination: true,
  })

  const navigateToArea = (row: any, event: React.MouseEvent) => {
    // Don't navigate if in loading state
    if (isLoading) return

    const area = row.original as Area

    event.stopPropagation()
    navigate(`/configurations/my_areas/${area.id}`)
  }

  // Card view for mobile rendering
  const renderMobileCard = (row: any) => {
    // For skeleton rows
    if (isLoading) {
      return (
        <div
          aria-rowindex={row.index + 2}
          className="mb-4 rounded-md border bg-card p-4 shadow-sm"
          key={row.id}
          role="row"
        >
          <div className="grid grid-cols-2 gap-2">
            {table.getVisibleLeafColumns().map((column) => (
              <div className="mb-2" key={column.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {typeof column.columnDef.header === 'string'
                    ? column.columnDef.header
                    : column.id}
                </div>
                <div className="flex h-5 items-center">
                  <Skeleton className="h-4 w-4/5" />
                </div>
              </div>
            ))}
          </div>
        </div>
      )
    }

    return (
      <div
        aria-rowindex={row.index + 2} // +2 for header row and 0-indexing
        className="mb-4 cursor-pointer rounded-md border bg-card p-4 shadow-sm hover:bg-accent/50"
        key={row.id}
        onClick={(e) => navigateToArea(row, e)}
        role="row"
      >
        <div className="grid grid-cols-2 gap-2">
          {row.getVisibleCells().map((cell: any) => {
            // Skip rendering empty cells
            if (cell.column.id === 'shipping' && !row.original.shipping)
              return null

            const header =
              typeof cell.column.columnDef.header === 'string'
                ? cell.column.columnDef.header
                : cell.column.id

            return (
              <div className="mb-2" key={cell.id} role="cell">
                <div className="text-xs font-medium text-muted-foreground">
                  {header}
                </div>
                <div>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card">
      <HeaderActions
        columns={table.getAllColumns() as unknown as Column<Area, unknown>[]}
      />

      <div className="border-t">
        <div>
          {isLoading ||
          isFetching ||
          isError ||
          table.getRowModel().rows?.length ? (
            isMobileView ? (
              // Mobile card view
              <div aria-label="Tabla de facturas" className="p-4" role="table">
                <div className="sr-only" role="rowgroup">
                  <div role="row">
                    {table.getHeaderGroups().map((headerGroup) =>
                      headerGroup.headers.map((header) => (
                        <div key={header.id} role="columnheader">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
                <div key={`page-${currentPage}`} role="rowgroup">
                  {table.getRowModel().rows.map((row) => renderMobileCard(row))}
                </div>
              </div>
            ) : (
              // Standard table view
              <div
                aria-label="Tabla de facturas"
                className="overflow-x-auto"
                role="region"
              >
                <Table>
                  <TableHeader>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <TableRow key={headerGroup.id}>
                        {headerGroup.headers.map((header) => {
                          return (
                            <TableHead
                              className="px-4 text-left"
                              key={header.id}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                  )}
                            </TableHead>
                          )
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody key={`page-${currentPage}`}>
                    {table.getRowModel().rows.map((row) => {
                      // For skeleton rows during loading
                      if (isLoading) {
                        return (
                          <TableRow className="h-11 text-left" key={row.id}>
                            {row.getVisibleCells().map((cell) => {
                              return (
                                <TableCell
                                  className="px-0.5 py-1 text-left"
                                  key={cell.id}
                                >
                                  <Skeleton className="h-10 w-full rounded-none bg-gray-100" />
                                </TableCell>
                              )
                            })}
                          </TableRow>
                        )
                      }

                      return (
                        <TableRow
                          className="cursor-pointer hover:bg-accent/50"
                          data-state={row.getIsSelected() && 'selected'}
                          key={row.id}
                          onClick={(e) => navigateToArea(row, e)}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <TableCell
                              className="px-4 py-1 text-left"
                              key={cell.id}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>
            )
          ) : (
            <div className="py-10 text-center">
              <p className="text-muted-foreground">
                {isError ? 'Error al cargar los datos' : 'No hay resultados'}
              </p>
            </div>
          )}
          <Navigation />
        </div>
      </div>
    </div>
  )
}
